import { InputNumber, InputNumberProps, message } from 'antd'
import type { ColumnType } from 'antd/es/table/interface'
import React, { useCallback } from 'react'

import { formatCurrencyNumber } from '../components/Money/Money'
import { TableFilterDropdownContainer } from '../components/TableFilterDropdownContainer/TableFilterDropdownContainer'
import { columnFilterIcon } from './useColumnFilters'

const formatter = (value?: string | number) =>
  value ? formatCurrencyNumber(value, 0) : ''

/**
 * client-side Ant Table columns filter by Number range.
 */
export function useNumberRangeFilter<T>() {
  const getNumberRangeFilter = useCallback(
    (
      dataIndex: keyof T | [keyof T, ...string[]],
      configMin?: InputNumberProps,
      configMax?: InputNumberProps,
      parseToNumber = false,
      getDynamicStep?: (value: number | null) => number
    ): ColumnType<T> => ({
      ...columnFilterIcon,
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
        close,
      }) => {
        let minValue: number | null = null
        let maxValue: number | null = null

        if (selectedKeys.length > 0 && typeof selectedKeys[0] === 'string') {
          try {
            const [min, max] = JSON.parse(selectedKeys[0])
            minValue = min !== null ? Number(min) : null
            maxValue = max !== null ? Number(max) : null
          } catch (error) {
            message.error('Chyba při zpracování číselného rozsahu')
          }
        }

        const updateSelectedKeys = (min: number | null, max: number | null) => {
          if (min === null && max === null) {
            setSelectedKeys([])
          } else {
            setSelectedKeys([JSON.stringify([min, max])])
          }
        }

        return (
          <TableFilterDropdownContainer
            clearFilters={clearFilters}
            confirm={confirm}
            close={close}
            isSelected={!!selectedKeys?.length}
          >
            <div className="space-y-2 w-52">
              <InputNumber
                placeholder={'Min (nepovinné)'}
                className="!w-full"
                onPressEnter={() => confirm()}
                min={0}
                formatter={formatter}
                step={getDynamicStep ? getDynamicStep(minValue) : undefined}
                {...configMin}
                value={minValue}
                onChange={value =>
                  updateSelectedKeys(
                    typeof value === 'number' ? value : null,
                    maxValue
                  )
                }
              />
              <InputNumber
                placeholder={'Max (nepovinné)'}
                className="!w-full"
                onPressEnter={() => confirm()}
                min={0}
                formatter={formatter}
                step={getDynamicStep ? getDynamicStep(maxValue) : undefined}
                {...configMax}
                value={maxValue}
                onChange={value =>
                  updateSelectedKeys(
                    minValue,
                    typeof value === 'number' ? value : null
                  )
                }
              />
            </div>
          </TableFilterDropdownContainer>
        )
      },
      onFilter: (value, record) => {
        if (!value || typeof value !== 'string') return false

        try {
          const [min, max] = JSON.parse(value)

          const getNestedValue = (obj: T, path: (string | number)[]) =>
            path.reduce(
              (acc, key) =>
                acc && typeof acc === 'object' && key in acc
                  ? (acc as Record<string | number, unknown>)[key]
                  : undefined,
              obj as unknown
            )

          const recordValue = parseToNumber
            ? Number(
                getNestedValue(
                  record,
                  (Array.isArray(dataIndex)
                    ? dataIndex
                    : [dataIndex]) as string[]
                )
              )
            : (getNestedValue(
                record,
                (Array.isArray(dataIndex) ? dataIndex : [dataIndex]) as string[]
              ) as unknown as number)

          if (recordValue === undefined || Number.isNaN(recordValue))
            return false

          if (min !== null && max !== null) {
            return recordValue >= min && recordValue <= max
          }

          if (min !== null) {
            return recordValue >= min
          }

          if (max !== null) {
            return recordValue <= max
          }

          return false
        } catch (error) {
          message.error('Chyba při filtrování podle číselného rozsahu')
          return false
        }
      },
    }),
    []
  )

  return { getNumberRangeFilter }
}
